import React, { useState, useEffect, useMemo } from 'react';
import { Stack, Group, Box } from '@mantine/core';
import { ChartWithTimelines } from '.';
import { DateRangeSelector } from './DateRangeSelector';
import { MetricsSelector } from './MetricsSelector';
import { AppMedWarning } from './AppMedWarning';
import { useParsedDates } from '../utils';
import { useDateRange, useChartData } from '../hooks';
import { t } from 'i18next';
import { CustomDateModal } from './CustomDateModal';
import { observer } from 'mobx-react';
import { inject } from 'mobx-react';
import { PatientStore } from '../../../../global/Store/PatientStore';
import { useGetQuestionnaires } from '../../../../global/entities/Questionnaires/queries/useGetQuestionnaires';
import { useQueries } from '@tanstack/react-query';
import { QueryKeys } from '../../../../global/queryDefaults/QueryKeys';
import { QuestionnaireAnswerApi } from '../../../../global/apis/QuestionnaireAnswerApi/QuestionnaireAnswerApi';
import { CLINICAL_METRICS_KEY as QUESTIONNAIRES_KEY, QUESTIONNAIRE_HIDDEN_SERIES_KEY, getSelectedMetrics, saveSelectedMetrics } from '../utils/localStorage';
import { LabMetricsSelector } from './LabMetricsSelector';
import { useGetLabTests } from '../../../../global/entities/LabTest/queries/useGetLabTests';
import { CHART_METRIC_COLORS, transformQuestionnaireScoreData } from '../utils';
import { groupLabTests } from '../utils';
import { useGetBiomarkers } from '../../../../global/entities/Biomarker/queries/useGetBiomarkers';

/**
 * Groups questionnaires for display as metrics
 */
function groupQuestionnaires(questionnaires: any[] = [], questionnaireCategories: Record<string, string[]> = {}) {
  const uniqueMetrics = new Map();
  
  Object.entries(questionnaires || []).forEach(([, questionnaire], index) => {
    const metricId = questionnaire.id;
    const shortName = questionnaire.shortName || '';
    const categories = questionnaireCategories[metricId.toString()] || ['Total'];
    
    // Create a metric for each category
    categories.forEach((category, categoryIndex) => {
      const metricKey = `{${metricId}-${shortName}}-${category}`;
      if (!uniqueMetrics.has(metricKey)) {
        uniqueMetrics.set(metricKey, {
          id: metricKey,
          questionnaireId: metricId.toString(),
          name: `${questionnaire.name} - ${category}`,
          display: `${category}`,
          color: CHART_METRIC_COLORS[(index * categories.length + categoryIndex) % CHART_METRIC_COLORS.length],
          description: `${questionnaire.name} - ${category}`,
          isQuestionnaire: true,
          category: category
        });
      }
    });
  });
  
  return Array.from(uniqueMetrics.values());
}

export type IQuestionnaireTabProps = {
  patientStore?: PatientStore;
};

export const QuestionnaireTab: React.FC<IQuestionnaireTabProps> = inject(
  'patientStore',
  'routeStore',
)(
  observer(({ patientStore }) => {
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(
    () => new Set(getSelectedMetrics(QUESTIONNAIRE_HIDDEN_SERIES_KEY)),
  );
  const [questionnaireMetricsOptions, setQuestionnaireMetricsOptions] = useState<{ value: string; label: string }[]>([]);
  const patientId = patientStore?.patientId;
  const { data: questionnaires } = useGetQuestionnaires(true, patientId ? Number(patientId) : undefined);

  const [selectedPkMetrics, setSelectedPkMetrics] = useState<string[]>([]);
  const [selectedExternalMetrics, setSelectedExternalMetrics] = useState<string[]>([]);
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const { data: fetchedLabTests } = useGetLabTests(patientStore?.patientId);
  const { data: biomarkers } = useGetBiomarkers();
  const labTests = useMemo(() => [...(fetchedLabTests || [])], [fetchedLabTests]);
  const labMetrics = useMemo(() => groupLabTests(labTests), [labTests]);

  // Calculate PK biomarker IDs for time averaging
  const pkBiomarkerIds = useMemo(() => {
    if (!biomarkers) return [];
    return biomarkers
      .filter(biomarker => biomarker.isPK)
      .map(biomarker => `lab-${biomarker.id}`);
  }, [biomarkers]);

  // Use queries for all questionnaires instead of just selected ones
  const questionnairesQueries = useQueries({
    queries: (questionnaires || []).map(questionnaire => ({
      queryKey: [QueryKeys.questionnaireScores, patientId, questionnaire.id],
      queryFn: async () => {
        if (!patientId || !questionnaire.id) return [];
        const result = await QuestionnaireAnswerApi.getQuestionnaireScoresByQuestionnaireId(
          patientId, 
          questionnaire.id.toString()
        );
        return result || [];
      },
      enabled: !!patientId && !!questionnaire?.id,
    }))
  });

  // Extract categories from questionnaire scores
  const questionnaireCategories = useMemo(() => {
    const categories: Record<string, Set<string>> = {};
    
    questionnairesQueries.forEach((query, index) => {
      if (query.data && questionnaires?.[index]) {
        const questionnaireId = questionnaires[index].id.toString();
        categories[questionnaireId] = new Set();
        
        query.data.forEach(score => {
          if (score.category) 
            categories[questionnaireId].add(score.category);
          
        });

        // If no categories found, add a default 'Total' category
        if (categories[questionnaireId].size === 0) 
          categories[questionnaireId].add('Total');
        
      }
    });

    return Object.fromEntries(
      Object.entries(categories).map(([id, categorySet]) => [
        id,
        Array.from(categorySet)
      ])
    );
  }, [questionnairesQueries, questionnaires]);
  
  // Combine all questionnaire scores data
  const questionnaireScoreData = useMemo(() => {
    const allScores: Array<{
      taskID: number;
      questionnaireId: number;
      dateCompleted: string;
      score: number;
      category?: string;
      scoreCount?: number;
      shortName?: string;
    }> = [];
    questionnairesQueries.forEach((query, idx) => {
      if (query.data && questionnaires?.[idx]) {
        const shortName = questionnaires[idx].shortName;
        query.data.forEach((score: any) => {
          allScores.push({ ...score, shortName });
        });
      } else if (query.data) {
        // fallback if no questionnaire match
        query.data.forEach((score: any) => {
          allScores.push({ ...score });
        });
      }
    });
    return allScores;
  }, [questionnairesQueries, questionnaires]);

  const chartMargins = { top: 5, right: 30, left: 20, bottom: 5 };
  
  const questionnaireMetrics = useMemo(() => {
    if (!questionnaires) return [];
    return groupQuestionnaires(questionnaires, questionnaireCategories);
  }, [questionnaires, questionnaireCategories]);

  // Add a state for totalGrouping
  const [totalGrouping] = useState(true);

  const transformedChartData = useMemo(
    () => transformQuestionnaireScoreData(questionnaireScoreData, labTests, startTime, endTime, totalGrouping, selectedPkMetrics, selectedExternalMetrics, pkBiomarkerIds),
    [questionnaireScoreData, labTests, startTime, endTime, totalGrouping, selectedPkMetrics, selectedExternalMetrics, pkBiomarkerIds]
  );

  const parsedDates = useParsedDates(transformedChartData);

  // Set initial metrics options
  useEffect(() => {
    if (!questionnaires) return;
    
    const uniqueQuestionnaires = questionnaires
      .filter(questionnaire => questionnaire.shortName) // Filter out questionnaires with null/undefined shortName
      .map(questionnaire => ({
        value: questionnaire.id.toString(),
        label: questionnaire.shortName || `Questionnaire ${questionnaire.id}` // Fallback label
      }));
    
    setQuestionnaireMetricsOptions(uniqueQuestionnaires);
  }, [questionnaires]);

  // Set initial selected metric when options are loaded and none are selected
  useEffect(() => {
    if (questionnaireMetricsOptions.length > 0) {
      const savedMetrics = getSelectedMetrics(QUESTIONNAIRES_KEY);
      if (savedMetrics.length > 0) {
        // Filter out any saved metrics that no longer exist
        const validSavedMetrics = savedMetrics.filter(metricId => 
          questionnaireMetricsOptions.some(metric => metric.value.toString() === metricId)
        );
        setSelectedMetrics(validSavedMetrics);
      }
    }
  }, [questionnaireMetricsOptions]);

  const {
    dateRangePreset,
    customDateRange,
    setShowCustomDateModal,
    showCustomDateModal,
    dateRange,
    handleDateRangeChange,
    handleCustomDateChange,
    dateRangePresets
  } = useDateRange({ parsedDates });

  const {
    processedChartData,
    allDates,
    getDatePosition,
  } = useChartData({
    chartData: transformedChartData,
    dateRange,
    parsedDates
  });
  
  const dateRangeDuration = dateRange.endDate && dateRange.startDate ? 
    Math.floor((dateRange.endDate.getTime() - dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24)) : 0;
  const xAxisFormat = dateRangeDuration > 90 ? 'month' : 'day';

  const handleMetricsChange = (selected: string[]) => {
    setSelectedMetrics(selected);
    saveSelectedMetrics(QUESTIONNAIRES_KEY, selected);
  };

  const handleLegendToggle = (metricId: string) => {
    setHiddenMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(metricId)) next.delete(metricId);
      else next.add(metricId);
      saveSelectedMetrics(QUESTIONNAIRE_HIDDEN_SERIES_KEY, Array.from(next));
      return next;
    });
  };

  // Filter metrics for selected questionnaires
  const selectedMetricsWithCategories = useMemo(() => {
    return questionnaireMetrics
      .filter(metric => selectedMetrics.includes(metric.questionnaireId))
      .map(metric => metric.id);
  }, [questionnaireMetrics, selectedMetrics]);

  return (
    <Stack gap="md">
      <Group align="flex-start" gap="lg">
        <AppMedWarning />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Stack gap="md">
            <DateRangeSelector
              dateRangePreset={dateRangePreset}
              onDateRangeChange={handleDateRangeChange}
              customDateRange={customDateRange}
              setShowCustomDateModal={setShowCustomDateModal}
              dateRangePresets={dateRangePresets}
            />
            
            <MetricsSelector
              metrics={questionnaireMetricsOptions}
              selectedMetrics={selectedMetrics}
              onChange={handleMetricsChange}
              label={t('graph.selectQuestionnaires')}
              placeholderKey="graph.questionnairesSelected"
            />
            
            <LabMetricsSelector
              selectedPkMetrics={selectedPkMetrics}
              onPkMetricsChange={setSelectedPkMetrics}
              selectedExternalMetrics={selectedExternalMetrics}
              onExternalMetricsChange={setSelectedExternalMetrics}
              startTime={startTime}
              endTime={endTime}
              onStartTimeChange={setStartTime}
              onEndTimeChange={setEndTime}
            />
          </Stack>
        </Box>
      </Group>
      
      <CustomDateModal
        opened={showCustomDateModal}
        onClose={() => setShowCustomDateModal(false)}
        dateRange={customDateRange}
        onDateChange={handleCustomDateChange}
      />
      
      <ChartWithTimelines 
        data={processedChartData}
        metrics={[...questionnaireMetrics, ...labMetrics]}
        selectedMetrics={[
          ...selectedMetricsWithCategories, 
          ...selectedPkMetrics,
          ...selectedExternalMetrics
        ]}
        chartMargins={chartMargins}
        getDatePosition={getDatePosition}
        allDates={allDates}
        xAxisDataKey="FormattedDate"
        xAxisFormat={xAxisFormat}
        hiddenMetrics={hiddenMetrics}
        onLegendToggle={handleLegendToggle}
      />
    </Stack>
  );
  })
); 